import { useLayoutEffect } from "react";
import { resolveAsset } from "../lib/api";

/**
 * Sets the per-post/per-share Open Graph + Twitter meta tags by mutating
 * the EXISTING static tags from index.html in place — never removing them
 * and never adding a second copy alongside them.
 *
 * Note on scrapers: WhatsApp, Facebook and Telegram do NOT execute
 * JavaScript when generating link previews, so these client-side updates
 * never reach them — they read whatever's in the static HTML response for
 * that route. The proper fix for those is server-side/edge injection per
 * route. This component still helps:
 *   - browser tab title + native share dialogs (Web Share API reads them)
 *   - Twitter / X (their card reader does run JS in some flows)
 *   - LinkedIn deep-share previews on iOS
 *   - Search engines and any bot renderer that executes JS and waits
 *     for the page to settle before snapshotting
 *
 * Why in-place mutation instead of react-helmet-async: Helmet inserts its
 * OWN new `<meta>` nodes alongside the static ones already in index.html
 * rather than replacing them, leaving two tags with the same
 * `property`/`name` (one stale default, one real) — which tag a given
 * parser reads for a duplicated property is not spec-guaranteed, so this
 * used to risk showing the wrong one. Editing the existing node's
 * `content` attribute directly avoids that ambiguity completely: there is
 * only ever one tag per property, its value is simply kept up to date.
 * `useLayoutEffect` runs as early as React can — synchronously before
 * paint — so callers (like the Lineup Builder share view) that already
 * know their title/description/image without waiting on any fetch get
 * the real values in place before that fetch even starts.
 */

const SITE_DEFAULTS = {
  title: "LAST — Let's All Sing Together",
  description:
    "letsallsingtogether.com · the independent Sky Blue hub. News, videos, the match centre and the #PUSB wall.",
  image: "https://letsallsingtogether.com/api/og/default.png",
};

function stripHtmlSnippet(html, max = 200) {
  if (!html) return "";
  const text = String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function absUrl(maybePath) {
  if (!maybePath) return "";
  if (/^https?:\/\//i.test(maybePath)) return maybePath;
  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "https://letsallsingtogether.com";
  // resolveAsset turns `/api/files/...` into a full URL via REACT_APP_BACKEND_URL
  // for assets; if it's already a relative `/path` we just prefix origin.
  const resolved = resolveAsset(maybePath);
  if (/^https?:\/\//i.test(resolved)) return resolved;
  return `${origin}${resolved.startsWith("/") ? "" : "/"}${resolved}`;
}

function setMetaContent(selector, value) {
  if (typeof document === "undefined" || !value) return;
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute("content", value);
}

export default function PostHelmet({
  title,
  description,
  image,
  url,
  type = "article",
}) {
  const finalTitle = (title && title.trim()) || SITE_DEFAULTS.title;
  const finalDescription =
    (description && stripHtmlSnippet(description)) || SITE_DEFAULTS.description;
  const finalImage = absUrl(image) || SITE_DEFAULTS.image;
  const finalUrl =
    url ||
    (typeof window !== "undefined" && window.location
      ? window.location.href
      : "");

  useLayoutEffect(() => {
    document.title = finalTitle;
    setMetaContent('meta[name="description"]', finalDescription);

    setMetaContent('meta[property="og:type"]', type);
    setMetaContent('meta[property="og:title"]', finalTitle);
    setMetaContent('meta[property="og:description"]', finalDescription);
    setMetaContent('meta[property="og:image"]', finalImage);
    setMetaContent('meta[property="og:image:alt"]', finalTitle);
    if (finalUrl) setMetaContent('meta[property="og:url"]', finalUrl);

    setMetaContent('meta[name="twitter:title"]', finalTitle);
    setMetaContent('meta[name="twitter:description"]', finalDescription);
    setMetaContent('meta[name="twitter:image"]', finalImage);
  }, [finalTitle, finalDescription, finalImage, finalUrl, type]);

  return null;
}
